import react, { Fragment, useContext, useEffect } from 'react';
import { ThemeContext } from 'styled-components';
import useSpaceConstrained from '../../hooks/useSpaceConstrained';
import useEventSink from '../../hooks/useEventSink';
import {useActiveRecord} from '../../hooks/index';
const rc = react.createElement;

/**
 * Because the detail pane is only for block organization in the BB UI 2.0
 * we can forgo creating an actual component and just use a Fragment.
 * If this is rendering in the context of a small mobile screen, then
 * we'll need to hide headers and similar, so set the SpaceConstrainedContext
 * accordingly.
 * @param {object} props
 * @returns Detail Pane Component
 */
export default function DetailPane(props) {
    const { hNode: { showForAction, namespace, relation, autoRefresh = false } = {} } = props;
    const [, setConstrained] = useSpaceConstrained();
    const { smallMobile } = useContext(ThemeContext);

    const [subscribe, publish] = useEventSink();

    const { record, formInstanceId } = useActiveRecord() ?? {};
    const activeRecordId = record?._id;

    useEffect(() => {
        if (autoRefresh && showForAction) {
            const refresh = payload => {
                if (!activeRecordId) return;
                if (
                    payload?._id === activeRecordId ||
                    payload?.record?._id === activeRecordId ||
                    payload?.items?.some(item => item._id === activeRecordId)
                ) {
                    // This event reloads data for the displayed record; it does not open
                    // a new form. ActiveRecord uses formRefreshInstanceId to update in place.
                    publish(
                        {_id: activeRecordId},
                        {
                            verb: showForAction,
                            namespace,
                            relation,
                            formRefreshInstanceId: formInstanceId
                        }
                    );
                }
            };
            const unsubscribes = [
                subscribe(
                    {
                        verb: 'upsert',
                        namespace,
                        relation,
                        status: 'success'
                    },
                    refresh
                ),
                subscribe(
                    {
                        verb: 'update',
                        namespace,
                        relation,
                        status: 'success'
                    },
                    refresh
                )
            ];
            return () => unsubscribes.forEach(u => u());
        }
    }, [activeRecordId, autoRefresh, formInstanceId, namespace, publish, relation, showForAction, subscribe]);

    useEffect(() => {
        setConstrained(smallMobile);
        return () => setConstrained(false);
    }, [setConstrained, smallMobile]);
    return rc(Fragment, null, props.children);
}
